import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  TableContext,
  TableData,
  TableFilterDataItem,
  TableHeaderCategoryConfig,
} from "./TableContext";
import {
  getTableDataWithSelection,
  toggleTableItemSelection,
  toggleTableSelection,
} from "../helpers/tableSelection";

type Props = {
  children: React.ReactNode;
  initialTableSearch?: object;
  initialTablePagination?: { page: number; rowsPerPage: number };
};

const TableProvider = ({
  children,
  initialTableSearch,
  initialTablePagination,
}: Props) => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [tableHeader, setTableHeader] = useState<TableHeaderCategoryConfig[]>(
    [],
  );
  const [tablePagination, setTablePagination] = useState({
    page: initialTablePagination?.page ?? 1,
    rowsPerPage: initialTablePagination?.rowsPerPage ?? 25,
  });
  const [tableSearch, setTableSearch] = useState<object | null>(
    initialTableSearch ?? null,
  );
  const [tableOrder, setTableOrder] = useState<object | null>(null);
  const [tableGlobalSearch, setTableGlobalSearch] = useState("");
  const [tableGlobalFilter, setTableGlobalFilter] = useState("");
  const [globalFilterOptions, setGlobalFilterOptions] = useState<
    null | TableFilterDataItem[]
  >(null);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  const [debouncedTableContextValues, setDebouncedTableContextValues] =
    useState<any>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTableContextValues({
        tableSearch,
        tablePagination,
      });
    }, 200);
    return () => {
      clearTimeout(handler);
    };
  }, [tablePagination, tableSearch]);

  const handleSetTablePagination = useCallback(
    (
      callBack: (prev: { page: number; rowsPerPage: number }) => {
        page: number;
        rowsPerPage: number;
      },
    ) => {
      setTablePagination(callBack);
    },
    [],
  );

  const deleteItem = useCallback((id: string | number) => {
    setTableData((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleTableSearch = useCallback(
    (
      key: string,
      value: string | number,
      options: { resetPage?: boolean } = {},
    ) => {
      if (
        typeof key === "string" &&
        (typeof value === "string" || typeof value === "number")
      ) {
        setTableSearch((prev: any) => {
          if (prev?.[key] === value) return prev; // value unchanged, no page reset
          if (prev?.[key] === undefined && value === "") return prev; // never set, empty value, skip

          if (options.resetPage !== false) {
            setTablePagination((p) => ({ ...p, page: 1 }));
          }
          return { ...prev, [key]: value };
        });
      }
    },
    [],
  );

  const handleTableOrder = useCallback((key: string) => {
    if (typeof key === "string") {
      // @ts-ignore
      setTableOrder((prev) => {
        // @ts-ignore
        if (prev && prev[key] === "asc") {
          return { [key]: "desc" };
        }
        return { [key]: "asc" };
      });
    }
  }, []);

  const handleCheckboxClick = useCallback(
    (
      crudData: any,
      checkboxId?: string | number,
      tableItemId?: string | number,
    ) => {
      const itemId = checkboxId ?? tableItemId ?? crudData?.id;
      if (itemId !== undefined && itemId !== null) {
        setSelectedItems((prev) =>
          toggleTableItemSelection(prev, itemId, crudData),
        );
      }
    },
    [],
  );

  const handleCheckAllItems = useCallback(() => {
    setSelectedItems((current) => toggleTableSelection(current, tableData));
  }, [tableData]);

  const tableDataWithCheckedItems = useMemo(
    () => getTableDataWithSelection(tableData, selectedItems),
    [tableData, selectedItems],
  );

  const contextValue = useMemo(
    () => ({
      tableData: tableDataWithCheckedItems,
      setTableData,
      tableHeader,
      setTableHeader,
      tablePagination,
      handleSetTablePagination,
      tableSearch,
      setTableSearch,
      handleTableSearch,
      tableOrder,
      handleTableOrder,
      deleteItem,
      tableGlobalSearch,
      setTableGlobalSearch,
      tableGlobalFilter,
      setTableGlobalFilter,
      globalFilterOptions,
      setGlobalFilterOptions,
      handleCheckboxClick,
      selectedItems,
      setSelectedItems,
      handleCheckAllItems,
      debouncedTableContextValues,
    }),
    [
      tableDataWithCheckedItems,
      tableHeader,
      tablePagination,
      tableSearch,
      tableOrder,
      tableGlobalSearch,
      tableGlobalFilter,
      globalFilterOptions,
      selectedItems,
      debouncedTableContextValues,
      handleSetTablePagination,
      handleTableSearch,
      handleTableOrder,
      deleteItem,
      handleCheckboxClick,
      handleCheckAllItems,
    ],
  );

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  );
};

export default TableProvider;
